
import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image';
import { getPostBySlug, getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service';
import { countryList, s3Static } from '../../../utils/constants';
import { BlogHeaderLinks } from '../../../components/blogs/BlogHeaderLinks';
import { blogsMaster } from '../../../utils/sg-blogs-data';
import { BlogsList } from '../../../components/blogs/BlogList';
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices, getWpAllHvacGuides, setWpAllHvacGuides } from '../../../service/wp-blogs-store';
import { relatedGuides } from 'utils/aircon-guide-clusters';
import { AirconGuideLinks } from '@/index/service-category/aircon-service/AirconGuideLinks';

// WordPress titles arrive HTML-encoded ("Chemical Wash vs Chemical Overhaul &#8212; ...");
// React would print the entities literally in the related-guide links.
const decodeEntities = (value) => String(value ?? '')
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#039;|&apos;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>')
import Link from 'next/link';
import { getRequestJSON } from '../../../api/index/location-api';
import { AcServiceLocality } from '../../../components/index/service-category/aircon-service/AcServiceLocality';
import { getAirconChemicalSlugData, getAirconGeneralSlugData, getAirconOverhaulSlugData, setAirconChemicalSlugData, setAirconGeneralSlugData, setAirconOverhaulSlugData } from '../../../service/page-slug-store';
import { airconCostCalculation } from '../../../js/aircon-cost';
import { AnimatePresence } from 'framer-motion';
import { AirconScope } from '../../../components/index/service-category/aircon-service/AirconScope';
import { useState } from 'react';
import { AirconHelp } from '../../../components/index/service-category/aircon-service/AirconHelp';
import { RequestProvider } from 'context/RequestContext';
import AirconRequestSection from '@/index/service-category/aircon-service/AirconRequestSection';
import AirconRequestSectionSlug, { AirconFormForSlug } from '@/index/service-category/aircon-service/AirconRequestSectionSlug';
import { Breadcrumb } from '@/commons/Breadcrumb';
import { buildBreadcrumb, breadcrumbJsonLd } from 'utils/breadcrumb';
import { organizationNode } from 'utils/local-business-schema';

export const getStaticPaths = async () => {
    const posts = await getWpNewPostsByCategory(100, "hvac-services");
    const oldPosts = await getWpPostsByCategory(100, "hvac-services");
    posts.push(...oldPosts)

    return {
      paths: posts.map((post) => `/sg/aircon-servicing-singapore/${post.slug}`),
      fallback: false,
    };
  };
  
export const getStaticProps = async ({ params }) => {
    const countryName = "Singapore"
    const currencyCd = "SGD"
    const post = await getPostBySlug(params?.slug);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    // Same set getStaticPaths renders, so every related link exists.
    if (getWpAllHvacGuides().length === 0) {
        const all = [
            ...(await getWpNewPostsByCategory(100, "hvac-services") ?? []),
            ...(await getWpPostsByCategory(100, "hvac-services") ?? []),
        ]
        setWpAllHvacGuides(all.map((p) => ({ slug: p.slug, title: decodeEntities(p.title) })))
    }
    const guides = [
        ...getWpAllHvacGuides(),
        ...airconBlogs.map((b) => ({ slug: b.url.split('/').pop(), title: b.title })),
    ]
    const relatedAirconGuides = relatedGuides(params?.slug, guides)

    const allRequestCategorySettings = await getRequestJSON("Singapore")
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const getLocalityList = () => {
        for(let x in countryList){
            if(countryList[x].country_name === countryName){
                return countryList[x].localityList
            }
        }
    } 

    const localityList = getLocalityList()

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []

    if(getAirconGeneralSlugData().length > 0 && getAirconChemicalSlugData().length > 0 && getAirconOverhaulSlugData().length > 0){
        standardServicing = getAirconGeneralSlugData()
        chemicalServicing = getAirconChemicalSlugData()
        overhaulServicing = getAirconOverhaulSlugData()
    } else {
        airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)
        setAirconGeneralSlugData(standardServicing)
        setAirconChemicalSlugData(chemicalServicing)
        setAirconOverhaulSlugData(overhaulServicing)  
    }

    return {
      props: {
        post,
        relatedAirconGuides,
        airconBlogs,
        wpPosts,
        wpNewPosts,
        requestCategorySettings,
        localityList,
        standardServicing,
        chemicalServicing,
        overhaulServicing,
        countryName,
        currencyCd
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 3 days
      revalidate: 259200, // In seconds
    };
};

// Built as an object and serialised, never interpolated into a JSON string:
// WordPress titles and excerpts contain quotes and line breaks, and a single
// `"` in a post title used to make the whole block invalid JSON. The trail
// comes from buildBreadcrumb so it matches the visible <Breadcrumb> exactly.
const buildPostJsonLd = (post, description) => {
    const path = `/sg/aircon-servicing-singapore/${post.slug}`
    const trail = buildBreadcrumb(path, post.title)
    const pageUrl = trail[trail.length - 1].url
    const image = post?.featuredImage?.node?.sourceUrl

    const article = {
        '@type': 'BlogPosting',
        '@id': `${pageUrl}/#article`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
        headline: post.title,
        description,
        author: { '@id': organizationNode['@id'] },
        publisher: { '@id': organizationNode['@id'] },
        datePublished: post.date,
        dateModified: post.modified,
        inLanguage: 'en-SG',
    }
    if (image) article.image = [image]

    return {
        '@context': 'https://schema.org',
        '@graph': [article, breadcrumbJsonLd(trail, pageUrl), organizationNode],
    }
}

export default function Home(props) {
    const post = props.post
    const description = post.excerpt.replace(/<[^>]+>/g, '').trim()

    const airconBlogs = props.airconBlogs
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts

    const requestCategorySettings = props.requestCategorySettings
    const localityList = props.localityList
    const standardServicing = props.standardServicing
    const chemicalServicing = props.chemicalServicing
    const overhaulServicing = props.overhaulServicing
    const currencyCd = props.currencyCd
    const countryName = props.countryName
    const requestType = 'Aircon Services'

    const [ isShowJobScope, setIsShowJobScope ] = useState(false)
    const [ serviceType, setServiceType ] = useState("general")
    const [ unit, setUnit ] = useState("")
    const [ unitType, setUnitType ] = useState("")
    const [ cost, setCost ] = useState("")
    
  return (
    <>
      <Head>
        <title>{post.title}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={description}/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"  content={`https://www.stringssg.com/sg/aircon-servicing-singapore/${post.slug}`} />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={post.title}/>
        <meta property="og:description" content={description} />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:modified_time" content={post.modified} />
        <meta property="og:image"  content={post?.featuredImage?.node?.sourceUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={post?.featuredImage?.node?.sourceUrl} />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content={`https://www.stringssg.com/sg/aircon-servicing-singapore/${post.slug}`}/>
        <meta name="twitter:title" content={post.title}/> 
        <meta name="twitter:description" content={description}/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={`https://www.stringssg.com/sg/aircon-servicing-singapore/${post.slug}`} />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildPostJsonLd(post, description)) }}
        />
      </Head>
      <div>
        <RequestProvider>
            <AirconRequestSectionSlug
                requestCategorySettings={requestCategorySettings}
                standardServicing={standardServicing}
                chemicalServicing={chemicalServicing}
                overhaulServicing={overhaulServicing}
                currencyCd={currencyCd}
                countryName={countryName}
                requestType={requestType}
                setIsShowJobScope={setIsShowJobScope}
                serviceType={serviceType}
                setServiceType={setServiceType}
                setUnit={setUnit}
                setUnitType={setUnitType}
                setCost={setCost}
                post={post}
                heroImage={post?.featuredImage?.node?.sourceUrl}
                title={post.title}
            />
        </RequestProvider>
        {/* jsonLd disabled: this page already emits a BreadcrumbList in its @graph above */}
        <Breadcrumb pageTitle={post.title} jsonLd={false} />
        <section className="py-10 space-y-10">
            <div className='max-w-5xl mx-auto px-2 space-y-10'>
              <div
                  className="post-content w-full px-2 mx-auto space-y-5 leading-8"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                  ></div>
            </div>
        </section>
        <AirconGuideLinks slug={post.slug} related={props.relatedAirconGuides} />
        <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
        <h2 className='bg-gray-800 text-white pt-10 pb-4 p-2 text-center text-2xl'>
          Topics
        </h2>
        <BlogHeaderLinks/>
      </div>
      {isShowJobScope &&
          <AnimatePresence>
             <AirconScope 
                setIsShowJobScope={setIsShowJobScope}
                serviceType={serviceType} 
                unit={unit} 
                unitType={unitType} 
                cost={cost} />
          </AnimatePresence>
      }
      <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}